import type { ReactNode } from "react";

import type { SelectProps } from "metabase/ui";
import { measureTextWidth } from "metabase/utils/measure-text";
import { memoize } from "metabase/utils/memoize";

// SP-2583: helpers to display schedule hours in the viewing user's local
// timezone (UTC is still the wire format; conversion is display-only).

export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const convertUTCHourToUserTimezone = (
  utcHour: number,
  userTimezone: string,
  minute: number = 0,
): number => {
  const utcDate = new Date(Date.UTC(2024, 0, 1, utcHour, minute, 0));
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: userTimezone,
    hour: "numeric",
    hour12: false,
  });
  return parseInt(formatter.format(utcDate), 10);
};

export const convertUserTimezoneHourToUTC = (
  userHour: number,
  userTimezone: string,
  minute: number = 0,
): number => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();

  for (let testUTCHour = 0; testUTCHour < 24; testUTCHour++) {
    const testDate = new Date(
      Date.UTC(year, month, day, testUTCHour, minute, 0),
    );
    const testHourInUserTZ = parseInt(
      new Intl.DateTimeFormat("en-US", {
        timeZone: userTimezone,
        hour: "numeric",
        hour12: false,
      }).format(testDate),
      10,
    );

    if (testHourInUserTZ === userHour) {
      return testUTCHour;
    }
  }

  const referenceUTC = new Date(Date.UTC(year, month, day, 12, 0, 0));
  const referenceInUserTZ = parseInt(
    new Intl.DateTimeFormat("en-US", {
      timeZone: userTimezone,
      hour: "numeric",
      hour12: false,
    }).format(referenceUTC),
    10,
  );
  const offset = referenceInUserTZ - 12;
  let utcHour = userHour - offset;
  if (utcHour < 0) {
    utcHour += 24;
  } else if (utcHour >= 24) {
    utcHour -= 24;
  }
  return utcHour;
};

export const formatTimeInTimezone = (
  hour: number,
  minute: number,
  timezone: string,
  hour12: boolean = true,
): string => {
  const date = new Date(Date.UTC(2024, 0, 1, hour, minute, 0));
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "2-digit",
    hour12,
  });
  return formatter.format(date);
};

export const formatUTCHourInUserTimezone = (
  utcHour: number | null | undefined,
  minute: number = 0,
  options: { hour12?: boolean; timezone?: string } = {},
): string => {
  if (utcHour === null || utcHour === undefined || isNaN(utcHour)) {
    return "";
  }
  const userTimezone = options.timezone || getUserTimezone();
  const use12Hour = options.hour12 ?? true;
  return formatTimeInTimezone(utcHour, minute, userTimezone, use12Hour);
};

export const combineConsecutiveStrings = (arr: ReactNode[]) => {
  return arr.reduce<ReactNode[]>((acc, node) => {
    const previousNode = acc.at(-1);
    if (typeof node === "string" && typeof previousNode === "string") {
      return [...acc.slice(0, acc.length - 1), previousNode + ` ${node}`];
    }
    if (typeof node === "string" && !node.trim()) {
      return acc;
    }
    return [...acc, typeof node === "string" ? node.trim() : node];
  }, []);
};

export const getLongestSelectLabel = (
  data: SelectProps<string | null>["data"] | { value: string }[] = [],
  fontFamily?: string,
): string => {
  const width = (str: string) =>
    measureTextWidthSafely(str, str.length, fontFamily);
  return [...data].reduce<string>((acc: string, option) => {
    let label: string;
    if (typeof option === "string") {
      label = option;
    } else if (!option) {
      label = "";
    } else if ("label" in option) {
      label = option.label;
    } else if ("group" in option) {
      label = getLongestSelectLabel(option.items);
    } else {
      label = "";
    }
    return width(label) > width(acc) ? label : acc;
  }, "");
};

/**
 * measureTextWidth can throw, so this returns defaultWidth instead.
 *
 * Pass the currently chosen font family:
 * ```
 *    const fontFamily = useSelector(state => getSetting(state, "application-font"));
 *    measureTextWidthSafely("string", 50, fontFamily);
 * ```
 *
 * The arguments are all primitives on purpose. The cache matches them by value,
 * and they come from a fixed set of schedule labels, so it cannot grow with
 * anything the user does.
 */
// eslint-disable-next-line metabase/no-module-level-memoize
export const measureTextWidthSafely = memoize(
  (text: string, defaultWidth: number, fontFamily?: string) => {
    try {
      return measureTextWidth(text, { family: fontFamily });
    } catch (e) {
      console.error(`Error while measuring text width:`, e);
      return defaultWidth;
    }
  },
);
